/**
 * @fileoverview Change Orders Component
 *
 * Displays project change orders with summary statistics and accordion list.
 * Includes:
 * - Summary cards showing total, approved, pending, cost impact, and time impact
 * - Accordion list with expandable details for each change order
 * - Documentation links for each change order
 *
 * @version 1.0.0
 */

import React, { useState, useMemo } from "react";
import {
  FaExchangeAlt,
  FaPlus,
  FaChevronDown,
  FaEdit,
  FaFilePdf,
  FaTrash,
  FaTimesCircle,
} from "react-icons/fa";
import * as Sentry from "@sentry/react";
import { functions } from "../../../../firebase";
import { httpsCallable } from "firebase/functions";
import { formatDateET } from "../../../../utils/dateUtils";
import DocumentViewerModal from "../../../../components/admin/DocumentViewerModal";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import "./ChangeOrders.css";

/**
 * Helper function to get status color class
 * @param {string} status - The change order status
 * @returns {string} CSS class name for status
 */
const getStatusColor = (status) => {
  switch (status) {
    case "Approved":
      return "status-approved";
    case "Submitted":
      return "status-submitted";
    case "Denied":
      return "status-denied";
    case "Completed":
      return "status-completed";
    default:
      return "";
  }
};

/**
 * Section wrapper component
 */
const Section = ({ title, icon, children, action }) => (
  <div className="apd-section">
    <div className="apd-section-header">
      <div className="apd-section-title">
        {icon && <span className="apd-section-icon">{icon}</span>}
        <h2>{title}</h2>
      </div>
      {action && <div className="apd-section-action">{action}</div>}
    </div>
    <div className="apd-section-content">{children}</div>
  </div>
);

/**
 * Change order accordion item component
 */
const ChangeOrderAccordion = ({
  co,
  isExpanded,
  onToggle,
  onEdit,
  onViewDoc,
  onDeleteDoc,
}) => (
  <div
    className={`apd-co-accordion status-${co.status.toLowerCase()}-border ${isExpanded ? "expanded" : ""}`}
  >
    {/* Accordion Header - Always visible */}
    <div className="apd-co-accordion-header" onClick={onToggle}>
      <div className="apd-co-header-left">
        <span className="apd-co-number">{co.coNumber}</span>
        <span className="apd-co-title">{co.title}</span>
      </div>
      <div className="apd-co-header-right">
        <div className="apd-co-header-impact">
          <span
            className={`apd-co-cost ${co.costImpact > 0 ? "positive" : ""}`}
          >
            {co.costImpact > 0 ? "+" : ""}${co.costImpact.toLocaleString()}
          </span>
          {co.timeImpact > 0 && (
            <span className="apd-co-time">+{co.timeImpact}d</span>
          )}
        </div>
        <span className={`apd-co-status ${getStatusColor(co.status)}`}>
          {co.status}
        </span>
        <span className={`apd-co-chevron ${isExpanded ? "expanded" : ""}`}>
          <FaChevronDown />
        </span>
      </div>
    </div>

    {/* Accordion Content - Shown when expanded */}
    {isExpanded && (
      <div className="apd-co-accordion-content">
        {/* Denial Reason Banner */}
        {co.status === "Denied" && co.denialReason && (
          <div className="apd-co-denial-banner">
            <FaTimesCircle className="apd-co-denial-icon" />
            <div className="apd-co-denial-content">
              <span className="apd-co-denial-label">Denial Reason</span>
              <p className="apd-co-denial-text">{co.denialReason}</p>
            </div>
          </div>
        )}
        {/* Description */}
        <div className="apd-co-detail-section">
          <h4>Description of Changes</h4>
          <div className="apd-co-detail-item">
            <p>{co.description}</p>
          </div>
        </div>

        {/* Details Grid */}
        <div className="apd-co-detail-section">
          <h4>Details</h4>
          <div className="apd-co-details-grid">
            <div className="apd-co-detail-item">
              <span className="apd-co-detail-label">Reason</span>
              <span className="apd-co-detail-value">{co.reason}</span>
            </div>
            <div className="apd-co-detail-item">
              <span className="apd-co-detail-label">Date Submitted</span>
              <span className="apd-co-detail-value">
                {formatDateET(co.dateSubmitted)}
              </span>
            </div>
            <div className="apd-co-detail-item">
              <span className="apd-co-detail-label">Cost Impact</span>
              <span className="apd-co-detail-value cost">
                {co.costImpact > 0 ? "+" : ""}${co.costImpact.toLocaleString()}
              </span>
            </div>
            <div className="apd-co-detail-item">
              <span className="apd-co-detail-label">Time Impact</span>
              <span className="apd-co-detail-value">
                {co.timeImpact > 0 ? `+${co.timeImpact} days` : "None"}
              </span>
            </div>
          </div>
        </div>

        {/* Attachments Row: Signed Documentation + Other Files */}
        {((co.signedDocumentation && co.signedDocumentation.length > 0) ||
          (co.otherFiles && co.otherFiles.length > 0) ||
          (co.filesPhotos && co.filesPhotos.length > 0)) && (
          <div className="apd-co-attachments-row">
            {/* Signed Documentation */}
            {co.signedDocumentation && co.signedDocumentation.length > 0 && (
              <div className="apd-co-detail-section">
                <h4>Signed Documentation</h4>
                <div className="apd-co-file-grid">
                  {co.signedDocumentation.map((doc) => {
                    const isPdf = doc.filename?.toLowerCase().endsWith(".pdf");
                    return (
                      <div
                        key={`signed-${doc.id}`}
                        className="apd-co-file-card-wrapper"
                      >
                        <button
                          className="apd-co-file-card"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewDoc(doc);
                          }}
                        >
                          <div className="apd-co-file-preview">
                            {isPdf ? (
                              <FaFilePdf className="apd-co-file-pdf-icon" />
                            ) : (
                              <img
                                src={doc.thumbnail || doc.url}
                                alt={doc.filename}
                                className="apd-co-file-thumb"
                              />
                            )}
                          </div>
                          <span className="apd-co-file-name">
                            {doc.filename}
                          </span>
                        </button>
                        <button
                          className="apd-co-file-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteDoc({
                              doc,
                              changeOrderId: co.id,
                              field: "signedDocumentation",
                            });
                          }}
                          aria-label={`Delete ${doc.filename}`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Other Files */}
            {((co.otherFiles && co.otherFiles.length > 0) ||
              (co.filesPhotos && co.filesPhotos.length > 0)) && (
              <div className="apd-co-detail-section">
                <h4>Other Files</h4>
                <div className="apd-co-file-grid">
                  {co.otherFiles &&
                    co.otherFiles.map((doc) => {
                      const isPdf = doc.filename
                        ?.toLowerCase()
                        .endsWith(".pdf");
                      return (
                        <div
                          key={`other-${doc.id}`}
                          className="apd-co-file-card-wrapper"
                        >
                          <button
                            className="apd-co-file-card"
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewDoc(doc);
                            }}
                          >
                            <div className="apd-co-file-preview">
                              {isPdf ? (
                                <FaFilePdf className="apd-co-file-pdf-icon" />
                              ) : (
                                <img
                                  src={doc.thumbnail || doc.url}
                                  alt={doc.filename}
                                  className="apd-co-file-thumb"
                                />
                              )}
                            </div>
                            <span className="apd-co-file-name">
                              {doc.filename}
                            </span>
                          </button>
                          <button
                            className="apd-co-file-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteDoc({
                                doc,
                                changeOrderId: co.id,
                                field: "otherFiles",
                              });
                            }}
                            aria-label={`Delete ${doc.filename}`}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      );
                    })}
                  {co.filesPhotos &&
                    co.filesPhotos.map((doc) => {
                      const isPdf = doc.filename
                        ?.toLowerCase()
                        .endsWith(".pdf");
                      return (
                        <div
                          key={`fp-${doc.id}`}
                          className="apd-co-file-card-wrapper"
                        >
                          <button
                            className="apd-co-file-card"
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewDoc(doc);
                            }}
                          >
                            <div className="apd-co-file-preview">
                              {isPdf ? (
                                <FaFilePdf className="apd-co-file-pdf-icon" />
                              ) : (
                                <img
                                  src={doc.thumbnail || doc.url}
                                  alt={doc.recordName || doc.filename}
                                  className="apd-co-file-thumb"
                                />
                              )}
                            </div>
                            <span className="apd-co-file-name">
                              {doc.recordName || doc.filename}
                            </span>
                          </button>
                          <button
                            className="apd-co-file-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteDoc({ doc, field: "filesPhotos" });
                            }}
                            aria-label={`Delete ${doc.recordName || doc.filename}`}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit Button */}
        <div className="apd-co-edit-btn-wrapper">
          <button
            className="apd-co-edit-btn"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(co);
            }}
          >
            <FaEdit /> Edit Change Order
          </button>
        </div>
      </div>
    )}
  </div>
);

/**
 * Main Change Orders Component
 *
 * @param {Object} props
 * @param {Array} props.changeOrders - Array of change order objects
 * @param {Function} props.onNewCO - Handler for creating new change order
 * @param {Function} props.onEditCO - Handler for editing change order
 */
const ChangeOrders = ({ changeOrders = [], onNewCO, onEditCO, onRefresh }) => {
  const [expandedCOId, setExpandedCOId] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Calculate summary statistics
  const coSummary = useMemo(() => {
    return {
      total: changeOrders.length,
      approved: changeOrders.filter(
        (co) => co.status === "Approved" || co.status === "Completed",
      ).length,
      pending: changeOrders.filter((co) => co.status === "Submitted").length,
      totalCostImpact: changeOrders
        .filter((co) => co.status !== "Denied")
        .reduce((sum, co) => sum + (co.costImpact || 0), 0),
      totalTimeImpact: changeOrders
        .filter((co) => co.status !== "Denied")
        .reduce((sum, co) => sum + (co.timeImpact || 0), 0),
    };
  }, [changeOrders]);

  const handleCOToggle = (coId) => {
    setExpandedCOId(expandedCOId === coId ? null : coId);
  };

  const handleCOEdit = (co) => {
    if (onEditCO) {
      onEditCO(co);
    } else {
      console.log("Edit change order:", co);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      if (deleteTarget.field === "filesPhotos") {
        // Files & Photos — remove the single attachment from the record
        const deleteFPAttachment = httpsCallable(
          functions,
          "deleteFilesPhotosAttachment",
        );
        await deleteFPAttachment({
          recordId: deleteTarget.doc.recordId,
          attachmentId: deleteTarget.doc.id,
        });
      } else {
        // Signed docs / other files are direct attachments on the CO record
        const deleteAttachment = httpsCallable(
          functions,
          "deleteChangeOrderAttachment",
        );
        await deleteAttachment({
          changeOrderId: deleteTarget.changeOrderId,
          field: deleteTarget.field,
          attachmentId: deleteTarget.doc.id,
        });
      }
      setDeleteTarget(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("[ChangeOrders] Delete error:", error);
      Sentry.captureException(error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Section
      title="Change Orders"
      icon={<FaExchangeAlt />}
      action={
        <button className="apd-add-btn" onClick={onNewCO}>
          <FaPlus /> <span className="hide-sm">New CO</span>
        </button>
      }
    >
      {/* CO Summary */}
      <div className="apd-co-summary">
        <div className="apd-co-summary-item">
          <span className="apd-co-summary-value">{coSummary.total}</span>
          <span className="apd-co-summary-label">
            Total <span className="hide-sm">Orders</span>
          </span>
        </div>
        <div className="apd-co-summary-item">
          <span className="apd-co-summary-value approved">
            {coSummary.approved}
          </span>
          <span className="apd-co-summary-label">Approved</span>
        </div>
        <div className="apd-co-summary-item">
          <span className="apd-co-summary-value pending">
            {coSummary.pending}
          </span>
          <span className="apd-co-summary-label">Pending</span>
        </div>
        <div className="apd-co-summary-item">
          <span className="apd-co-summary-value cost">
            +${coSummary.totalCostImpact.toLocaleString()}
          </span>
          <span className="apd-co-summary-label">Cost Impact</span>
        </div>
        <div className="apd-co-summary-item">
          <span className="apd-co-summary-value time">
            +{coSummary.totalTimeImpact} days
          </span>
          <span className="apd-co-summary-label">Time Impact</span>
        </div>
      </div>

      {/* CO Accordion List */}
      <div className="apd-co-list">
        {changeOrders.length > 0 ? (
          changeOrders.map((co) => (
            <ChangeOrderAccordion
              key={co.id}
              co={co}
              isExpanded={expandedCOId === co.id}
              onToggle={() => handleCOToggle(co.id)}
              onEdit={handleCOEdit}
              onViewDoc={setSelectedDocument}
              onDeleteDoc={setDeleteTarget}
            />
          ))
        ) : (
          <p className="apd-empty-message">HOORAY! No change orders...yet.</p>
        )}
      </div>

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <DocumentViewerModal
          doc={selectedDocument}
          onClose={() => setSelectedDocument(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete File?"
        message={`Are you sure you want to delete`}
        item={deleteTarget?.doc?.recordName || deleteTarget?.doc?.filename}
        punc="?"
        warning={
          "This will permanently remove this file from the change order."
        }
        proceed="This action cannot be undone."
        confirmText="Delete"
        isDestructive
        isLoading={isDeleting}
      />
    </Section>
  );
};

export default ChangeOrders;
